class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.resizeUpsell();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
        this.resizeUpsell();
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.syncExpiryStorage();
  }

  resizeUpsell() {
    this.querySelectorAll('cart-drawer-upsell').forEach((upsell) => {
      if (typeof upsell.resize === 'function') upsell.resize();
    });
  }

  syncExpiryStorage() {
    if (this.classList.contains('is-empty') || this.querySelector('cart-drawer-items.is-empty')) {
      sessionStorage.removeItem('cart-drawer-expiry-ends-at');
    }
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.syncExpiryStorage();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartDrawerUpsell extends HTMLElement {
  connectedCallback() {
    this.portalModals();
    this.initFlickity();
  }

  disconnectedCallback() {
    if (this.flickity) {
      this.flickity.destroy();
      this.flickity = null;
    }

    if (this._portedModals) {
      this._portedModals.forEach((modal) => modal.remove());
      this._portedModals = null;
    }
  }

  portalModals() {
    const modals = this.querySelectorAll('quick-add-modal');
    if (!modals.length) return;

    this._portedModals = [];
    modals.forEach((modal) => {
      document.body.appendChild(modal);
      this._portedModals.push(modal);
    });
  }

  initFlickity(attempt = 0) {
    const carousel = this.querySelector('.cart-drawer-upsell__carousel');
    if (!carousel) return;

    const cells = carousel.querySelectorAll('.cart-drawer-upsell__cell');
    if (cells.length === 0) return;

    if (typeof Flickity === 'undefined') {
      if (attempt < 40) {
        setTimeout(() => this.initFlickity(attempt + 1), 100);
      }
      return;
    }

    if (this.flickity) {
      this.flickity.destroy();
      this.flickity = null;
    }

    this.flickity = new Flickity(carousel, {
      cellAlign: 'left',
      contain: true,
      pageDots: false,
      prevNextButtons: false,
      draggable: cells.length > 1,
      wrapAround: false,
      adaptiveHeight: true,
      friction: 0.28,
      selectedAttraction: 0.025,
    });

    this.bindControls();
  }

  bindControls() {
    if (!this.flickity) return;

    const prevButton = this.querySelector('.cart-drawer-upsell__arrow--prev');
    const nextButton = this.querySelector('.cart-drawer-upsell__arrow--next');
    const dotsContainer = this.querySelector('.cart-drawer-upsell__dots');

    if (prevButton) {
      prevButton.onclick = () => this.flickity.previous();
    }

    if (nextButton) {
      nextButton.onclick = () => this.flickity.next();
    }

    if (dotsContainer) {
      dotsContainer.innerHTML = '';
      this.flickity.cells.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'cart-drawer-upsell__dot';
        if (index === this.flickity.selectedIndex) dot.classList.add('is-selected');
        dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
        dot.addEventListener('click', () => this.flickity.select(index));
        dotsContainer.appendChild(dot);
      });
    }

    const syncControls = () => {
      const index = this.flickity.selectedIndex;
      const lastIndex = this.flickity.cells.length - 1;

      if (prevButton) prevButton.disabled = index === 0;
      if (nextButton) nextButton.disabled = index === lastIndex;

      if (dotsContainer) {
        dotsContainer.querySelectorAll('.cart-drawer-upsell__dot').forEach((dot, i) => {
          dot.classList.toggle('is-selected', i === index);
        });
      }
    };

    this.flickity.on('select', syncControls);
    syncControls();
  }

  resize() {
    if (this.flickity) {
      this.flickity.resize();
    } else {
      this.initFlickity();
    }
  }
}

customElements.define('cart-drawer-upsell', CartDrawerUpsell);

class CartShippingProtection extends HTMLElement {
  connectedCallback() {
    this.checkbox = this.querySelector('.cart-drawer__shipping-protection-checkbox');
    this.priceEl = this.querySelector('.cart-drawer__shipping-protection-price');
    this.variantId = Number(this.dataset.variantId);
    this.lineKey = this.dataset.lineKey || null;
    this.isUpdating = false;

    if (!this.checkbox || !this.variantId) return;

    this.checkbox.addEventListener('change', this.onToggle.bind(this));

    if (!this.priceEl?.textContent.trim()) {
      this.loadPrice();
    }
  }

  async loadPrice() {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}products.json?limit=250`);
      if (!response.ok) return;

      const data = await response.json();
      const match = (data.products || [])
        .flatMap((product) => product.variants.map((variant) => ({ product, variant })))
        .find(({ variant }) => variant.id === this.variantId);

      if (!match) return;

      const titleEl = this.querySelector('.cart-drawer__shipping-protection-title');
      if (titleEl && this.dataset.active !== 'true') {
        titleEl.textContent = match.product.title;
      }

      this.priceEl.textContent = this.formatMoney(match.variant.price);
    } catch (error) {
      console.error(error);
    }
  }

  formatMoney(amount) {
    const value = Number(amount);
    if (Number.isNaN(value)) return '';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'EUR',
      }).format(value);
    } catch (error) {
      return `€${value.toFixed(2)}`;
    }
  }

  getCart() {
    return document.querySelector('cart-drawer');
  }

  getSections() {
    const cart = this.getCart();
    if (cart && typeof cart.getSectionsToRender === 'function') {
      return cart.getSectionsToRender().map((section) => section.id);
    }
    return ['cart-drawer', 'cart-icon-bubble'];
  }

  refreshSections(data) {
    const cart = this.getCart();
    if (!cart || !data.sections) return;

    cart.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement || !data.sections[section.id]) return;
      sectionElement.innerHTML = cart.getSectionInnerHTML(data.sections[section.id], section.selector);
    });

    cart.querySelector('#CartDrawer-Overlay')?.addEventListener('click', cart.close.bind(cart));
  }

  async onToggle() {
    if (this.isUpdating) return;

    this.isUpdating = true;
    this.classList.add('is-loading');
    this.checkbox.disabled = true;

    try {
      if (this.checkbox.checked) {
        await this.addProtection();
      } else {
        await this.removeProtection();
      }
    } catch (error) {
      console.error(error);
      this.checkbox.checked = !this.checkbox.checked;
    } finally {
      this.isUpdating = false;
      this.classList.remove('is-loading');
      if (this.checkbox) this.checkbox.disabled = false;
    }
  }

  async addProtection() {
    const response = await fetch(`${routes.cart_add_url}`, {
      ...fetchConfig('javascript'),
      body: JSON.stringify({
        id: this.variantId,
        quantity: 1,
        sections: this.getSections(),
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json();

    if (data.status) {
      throw new Error(data.description || data.message || 'Unable to add shipping protection');
    }

    const cart = this.getCart();
    if (cart && typeof cart.renderContents === 'function') {
      cart.renderContents(data);
    } else {
      this.refreshSections(data);
    }

    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'shipping-protection',
      cartData: data,
      variantId: this.variantId,
    });
  }

  async removeProtection() {
    let lineKey = this.lineKey;

    if (!lineKey) {
      const cartState = await fetch(`${routes.cart_url}.js`).then((res) => res.json());
      const match = (cartState.items || []).find((item) => item.variant_id === this.variantId);
      lineKey = match?.key;
    }

    if (!lineKey) return;

    const response = await fetch(`${routes.cart_change_url}`, {
      ...fetchConfig('javascript'),
      body: JSON.stringify({
        id: lineKey,
        quantity: 0,
        sections: this.getSections(),
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json();

    if (data.errors) {
      throw new Error(data.errors);
    }

    this.refreshSections(data);

    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'shipping-protection',
      cartData: data,
      variantId: this.variantId,
    });
  }
}

customElements.define('cart-shipping-protection', CartShippingProtection);

class CartExpiryTimer extends HTMLElement {
  connectedCallback() {
    this.duration = Number(this.dataset.duration || 300) * 1000;
    this.storageKey = this.dataset.storageKey || 'cart-drawer-expiry-ends-at';
    this.timeEl = this.querySelector('.cart-drawer__expiry-time');

    if (!this.timeEl) return;

    this.ensureEndTime();
    this.tick();
    this.intervalId = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    if (this.intervalId) clearInterval(this.intervalId);
  }

  ensureEndTime() {
    const existing = Number(sessionStorage.getItem(this.storageKey) || 0);
    const now = Date.now();

    if (!existing || existing <= now) {
      sessionStorage.setItem(this.storageKey, String(now + this.duration));
    }
  }

  tick() {
    const endsAt = Number(sessionStorage.getItem(this.storageKey) || 0);
    const remaining = Math.max(0, Math.floor((endsAt - Date.now()) / 1000));
    const minutes = String(Math.floor(remaining / 60)).padStart(2, '0');
    const seconds = String(remaining % 60).padStart(2, '0');

    this.timeEl.textContent = `${minutes}:${seconds}`;

    if (remaining === 0 && this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}

customElements.define('cart-expiry-timer', CartExpiryTimer);
